import { useState, useEffect, useMemo, useRef } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { handleTokenAndPostRequest } from './utils'; // adjust path as needed

const HOMEPRICE_DOMAINS = ['homeprice.site', 'homeprice.app'];
// const LOCAL_DEV_DOMAIN = 'pointresidential.agency';
const LOCAL_DEV_DOMAIN = null;

function getEffectiveDomain() {
  const hostname = window.location.hostname;
  if (hostname === 'localhost') return LOCAL_DEV_DOMAIN;
  // Strip www. and any subdomain, keep root domain (e.g. "www.pointresidential.agency" → "pointresidential.agency")
  const parts = hostname.replace(/^www\./, '').split('.');
  const rootDomain = parts.length > 2 ? parts.slice(-2).join('.') : parts.join('.');
  if (HOMEPRICE_DOMAINS.includes(rootDomain)) return null;
  return rootDomain;
}

const DEFAULT_BRANDING = { accentColor: '#2563eb', buttonTextColor: '#fff', isLight: false, iconColor: '#2563eb', iconBgColor: '#2563eb', isWhiteLabel: false };

function computeBranding(details) {
  if (!details) return DEFAULT_BRANDING;

  const domain = getEffectiveDomain() || details.custom_domain;

  const isWhiteLabel = domain
    && !HOMEPRICE_DOMAINS.includes(domain);

  const accentColor = details.agent_colour_lightness === 'dark'
    ? (details.agent_colour || '#2563eb')
    : (details.agent_button_colour || '#2563eb');

  const isLightVal = details.agent_colour_lightness === 'light';
  const buttonTextColor = isLightVal ? '#fff' : (details.agent_button_text_colour || '#fff');
  const iconColor = isLightVal ? '#111' : accentColor;
  // Icon background always uses agent_colour so light agencies get branded tinted backgrounds
  const iconBgColor = details.agent_colour || accentColor;

  return { accentColor, buttonTextColor, isLight: isLightVal, iconColor, iconBgColor, isWhiteLabel };
}

export function useAgentDetails() {
  const location = useLocation();
  const [searchParams] = useSearchParams();

  // Only use path param if it's one of the allowed slugs
  const pathSegments = location.pathname.split('/').filter(Boolean);
  const lastSegment = pathSegments[pathSegments.length - 1];
  const allowedSlugs = ['stylux-properties', 'spica-real-estate'];

  const customDomain = getEffectiveDomain();
  const explicitSlug = allowedSlugs.includes(lastSegment)
    ? lastSegment
    : searchParams.get('agent');

  // If on a custom domain with no explicit agent param, let the backend resolve via domain
  const agent_slug = explicitSlug || (customDomain ? null : 'home-price');
  // Cache key: use slug when available, otherwise the custom domain
  const cacheKey = agent_slug || customDomain;

  const [agentDetails, setAgentDetails] = useState(() => {
    const storedAgentDetails = sessionStorage.getItem('agentDetails');
    const storedKey = sessionStorage.getItem('landing_page_slug');
    if (storedAgentDetails && storedKey === cacheKey) {
      const parsed = JSON.parse(storedAgentDetails);
      if (parsed && parsed.landing_page_slug === 'home-price') {
        parsed.agent_profile_image = null;
        parsed.agent_name = null;
        parsed.agent_title = null;
        parsed.agency_name_short = null;
        parsed.agency_name = null;
      }
      return parsed;
    }
    return null;
  });

  // Synchronously clear stale branding when agent changes (before paint)
  const prevKeyRef = useRef(cacheKey);
  if (prevKeyRef.current !== cacheKey) {
    prevKeyRef.current = cacheKey;
    if (agentDetails) setAgentDetails(null);
  }

  useEffect(() => {
    const storedKey = sessionStorage.getItem('landing_page_slug');
    const storedAgentDetails = JSON.parse(sessionStorage.getItem('agentDetails') || 'null');

    if (storedAgentDetails && storedKey === cacheKey) {
      if (storedAgentDetails && storedAgentDetails.landing_page_slug === 'home-price') {
        storedAgentDetails.agent_profile_image = null;
        storedAgentDetails.agent_name = null;
        storedAgentDetails.agent_title = null;
        storedAgentDetails.agency_name_short = null;
        storedAgentDetails.agency_name = null;
      }
      setAgentDetails(storedAgentDetails);
    } else {
      setAgentDetails(null);   // clear stale branding immediately
      const fetchAgentDetails = async () => {
        const url = `${window.api_host}/agency`;
        const payload = agent_slug
          ? { agent_slug }
          : { custom_domain: customDomain };
        try {
          const response = await handleTokenAndPostRequest(
            url,
            payload,
            { 'Content-Type': 'application/json' }
          );
          const data = response.data;
          if (data && data.landing_page_slug === 'home-price') {
            data.agent_profile_image = null;
            data.agent_name = null;
            data.agent_title = null;
          }
          setAgentDetails(data);
          sessionStorage.setItem('agentDetails', JSON.stringify(data));
          sessionStorage.setItem('landing_page_slug', cacheKey);
        } catch (error) {
          console.error('Error:', error);
        }
      };
      fetchAgentDetails();
    }
  }, [agent_slug]);

  const branding = useMemo(() => computeBranding(agentDetails), [agentDetails]);

  return { agent_slug: agentDetails?.landing_page_slug || agent_slug, agentDetails, setAgentDetails, branding };
}

export { computeBranding, getEffectiveDomain };